import { yupResolver } from "@hookform/resolvers/yup";
import {
  podStep1OnboardingValidations,
  podStep2OnboardingValidations,
  podStep3OnboardingValidations,
} from "./validationSchemas";
import { Step3FieldsInterface } from "./types";
import { useFieldArray, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DUPLICATE_USER_ENTRY_ERROR, EMAIL_ERROR, EMAIL_VALIDATION_FAILED, POD_ERROR, POD_VALIDATION_FAILED, questionList } from "utility/constants";
import { callToLoadPodOnboardingFormInitialData, callToSubmitPodOnboarding, setIsPodOnboardFormSubmited } from "./slice";
import { getIsPodOnboardFormSubmited, getPodList, getRoles } from "./selectors";
import { useDispatch, useSelector } from "react-redux";
import { RoleItem, ServicesEnum } from "utility/types";
import { toBase64 } from "utility/helper";
import { RootState } from "api-store/store";
import { reactToastify, ToastType } from "utility/reactToastify";
import { validateEmailCustomYupValidation } from "pages/mlops/ducks/utils";

export const usePodOnboarding = ({ isSkipServerValidation }: { isSkipServerValidation?: boolean }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const step1InitialFormData: {
    pod: string,
    ciName: string,
    description: string,
    businessVertical: string,
    podAmbassadorEmail: string,
    architectureDiagramLink?: string,
    architectureDiagramFile?: File[],
    podFullName: string;
    is_wandb_onboarding_enabled: boolean;
  } = {
    pod: "",
    ciName: "",
    description: "",
    businessVertical: "",
    podAmbassadorEmail: "",
    architectureDiagramLink: undefined,
    architectureDiagramFile: undefined,
    podFullName: "",
    is_wandb_onboarding_enabled: true
  };
  const step2InitialFormData = {
      projectGoals: "",
      typeOfMLProblem: "",
      endUser: "",
      typeOfDataAndStorage: "",
      privacyAndCompliance: "",
      frameworksOrLibraries: "",
      computeRequirment: "",
      deploymentStrategy: "",
      performanceMonitor: "",
      usageCost: ""
    };
  const [step1Data, setStep1Data] = useState(step1InitialFormData);
  const [step2Data, setStep2Data] = useState(step2InitialFormData);
  const isPodOnboardFormSubmited = useSelector(getIsPodOnboardFormSubmited);
  const userData = useSelector((state: RootState) => state.userData);
  const [usersList, setUsersList] = useState<{email?: string, roles: string[]}[]>([]);
  const [isValidatingPod, setIsValidatingPod] = useState(false);
  const [podError, setPodError] = useState("");
  const [podAmbassadorEmailError, setPodAmbassadorEmailError] = useState("");
  const [isValidatingPodAmbassadorEmail, setIsValidatingPodAmbassadorEmail] = useState(false);
  const podList = useSelector(getPodList);
  const roles = useSelector(getRoles);
  const isMounted = useRef(false);

  useEffect(() => {
    if(isPodOnboardFormSubmited) {
      dispatch(setIsPodOnboardFormSubmited(false));
      navigate("/track-requests");
    }
  }, [isPodOnboardFormSubmited, dispatch, navigate]);
  

  const {
    register: step1Register,
    handleSubmit: step1HandleSubmit,
    formState: formStateStep1,
    watch: step1Watch,
    setValue: step1SetValue,
    getValues: getValuesStep1,
    setFocus: setFocusStep1,
  } = useForm({
    resolver: yupResolver(podStep1OnboardingValidations(isSkipServerValidation)),
    values: step1InitialFormData,
    mode: "onChange"
  });
  const { errors: step1Errors, isValidating: isValidatingStep1, validatingFields: validatingFieldsStep1, isValid: isValidStep1 } = formStateStep1;
  const {
    register: step2Register,
    handleSubmit: step2HandleSubmit,
    formState: { errors: step2Errors },
    setValue: step2SetValue,
  } = useForm({
    resolver: yupResolver(podStep2OnboardingValidations),
    values: step2InitialFormData,
    mode: "onChange",
  });

  const {
    register: step3Register,
    handleSubmit: step3HandleSubmit,
    formState: { errors: step3Errors, isSubmitted: isStep3Submitted },
    control: step3Control,
    watch: step3Watch,
    setError: setStep3Error,
    clearErrors: clearStep3Errors,
    setValue: step3SetValue
  } = useForm<Step3FieldsInterface>({
    resolver: yupResolver(podStep3OnboardingValidations),
    defaultValues: {
      users: [],
      addUserLater: false,
    },
    mode: "onChange"
  });

  const userFields = step3Watch("users");
  const addUserLater = step3Watch("addUserLater");
  
  const {
    append: addUser,
    remove: removeUser,
  } = useFieldArray({
    name: "users",
    control: step3Control,
  });

  useEffect(() => {
    if (!isMounted.current && !addUserLater) {
      isMounted.current = true;
      if ((!roles || roles.length <= 0) || (!podList || podList.length <= 0)) {
        dispatch(callToLoadPodOnboardingFormInitialData());
      }
    }
  }, [dispatch, roles, podList, addUserLater]);

  const registerFunctions = { 1: step1Register, 2: step2Register, 3: step3Register };

  const errorList = [step1Errors, step2Errors, step3Errors];
  const submitFunctions = [
    step1HandleSubmit,
    step2HandleSubmit,
    step3HandleSubmit,
  ];

  const errors = errorList[step - 1];
  const register = registerFunctions[step as keyof typeof registerFunctions];
  const handleSubmit = submitFunctions[step - 1];

  const addUserHandler = () => {
    addUser({ email: "", roles: [] });
  };

  const removeUserHandler = (removeIndex: number | Array<number>) => {
    return () => removeUser(removeIndex);
  };

  const architectureDiagramFile = step1Watch("architectureDiagramFile");
  const onClickClearArchitectureFile = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.stopPropagation();
    step1SetValue("architectureDiagramFile", "");
  };

  const step1OnSubmit = (formData: any) => {
    if(podError || isValidatingPod) {
      setFocusStep1("pod")
      return;
    }
    if(podAmbassadorEmailError || isValidatingPodAmbassadorEmail) {
      setFocusStep1("podAmbassadorEmail")
      return;
    }
    nextStep();
    setStep1Data(formData);
  };

  const step2OnSubmit = (formData: any) => {
    nextStep();
    setStep2Data(formData);
  };

  const generateBusinessJustification = () => {
    const questions = questionList.map((quesitonItem) => quesitonItem.questions).flat();
    const questionAnswersItems = questions.map((quesitonItem, index) => {
      return `${index + 2}. ${quesitonItem.label}\n ${step2Data[quesitonItem.field as keyof typeof step2Data]}`
    });
    return `
Business vertical: ${step1Data.businessVertical}
CI Name: ${step1Data.ciName}
${questionAnswersItems.join("\n")}
      `;
  }

  const step3OnSubmit = async (formData: {
    users?: {
      email?: string;
      roles: string[];
    }[]
  }) => {
    // check if any duplicate user (same email with overlapping roles)
    const isDuplicateUser = formData?.users?.some((user) => {
      const duplicateUserEntries = formData?.users?.filter((userItem) => 
        userItem.email === user.email && 
        userItem.roles.some((r) => user.roles.includes(r))
      )
      return duplicateUserEntries && duplicateUserEntries?.length > 1
    })
    if(isDuplicateUser) {
      reactToastify({ type: ToastType.error, message: DUPLICATE_USER_ENTRY_ERROR });
      return;
    }
    const architectureDiagramFile = step1Data.architectureDiagramFile &&  step1Data.architectureDiagramFile.length > 0 ? await toBase64(step1Data.architectureDiagramFile[0]) : "";
    dispatch(callToSubmitPodOnboarding({
      v_tdsp_point_of_contact_new: step1Data.podAmbassadorEmail as string,
      v_business_justification: generateBusinessJustification(),
      v_tdsp_description: step1Data.description,
      v_tdsp_team_business_name_needed: step1Data.pod.toLowerCase(),
      v_notes_comments: formData?.users ? getComments(formData?.users) : "",
      users: formData?.users ? expandUsersWithMultipleRoles(formData.users) : [],
      architecture_diagram_file: architectureDiagramFile,
      v_opened_by: userData.user_name.replace("tmnatest", "toyota"),
      pod_name_abbr: step1Data.pod.toLowerCase().trim().replace(" ", ""),
      v_is_wandb_onboarding_enabled: step1Data.is_wandb_onboarding_enabled
    }));
  };

  const getComments = (users: {
    email?: string;
    roles: string[];
  }[]) => {
    return users.map((formItem) => `Email: ${formItem.email} Roles: ${formItem.roles.join(", ")}`).join("\n")
  }

  // Expand users with multiple roles into separate entries for backend
  const expandUsersWithMultipleRoles = (users: { email?: string; roles: string[] }[]) => {
    const expandedUsers: { email: string; role: string }[] = [];
    users.forEach((user) => {
      if (user.roles && user.roles.length > 0) {
        user.roles.forEach((role) => {
          expandedUsers.push({ email: user.email || "", role });
        });
      }
    });
    return expandedUsers;
  }

  const MAX_STEP = 3;

  const nextStep = () => { if (step < MAX_STEP) { setStep(step + 1); } };

  const onValidFieldSubmitFunctions = [
    step1OnSubmit,
    step2OnSubmit,
    step3OnSubmit,
  ];

  const onSubmit = onValidFieldSubmitFunctions[step - 1];

  const handleGoBack = () => {
    if (step > 1) {
      setStep(step - 1);
      return;
    }
    navigate("/self-service-portal");
  };

  const updateUserList = (arg: {email?: string; roles: string[]}[]) => {
    setUsersList(arg)
  }

  const validatePodAbbreviation = async (pod: string) => {
    try {
      if(isSkipServerValidation) { return; }
      if(step1Errors && step1Errors?.pod && step1Errors?.pod?.message) { return; }
      setIsValidatingPod(true);
      setPodError("");
      const isValidPod = !podList.some((podItem) => podItem.u_primary.toLowerCase() === pod.toLowerCase());
      if(!isValidPod) { setPodError(POD_ERROR); }
      else { setPodError(""); }
    } catch(error) {
      console.log("Error", error);
      setPodError(POD_VALIDATION_FAILED);
    } finally {
      setIsValidatingPod(false);
    }
  }

  const validatePodAmbassadorEmail = async (email: string) => {
    try {
      if(isSkipServerValidation) { return; }
      if(step1Errors && step1Errors?.podAmbassadorEmail && step1Errors?.podAmbassadorEmail?.message) { return; }
        setIsValidatingPodAmbassadorEmail(true);
        setPodAmbassadorEmailError("");
      const isValidPod = await validateEmailCustomYupValidation(email);
      if(!isValidPod) { setPodAmbassadorEmailError(EMAIL_ERROR); }
      else { setPodAmbassadorEmailError(""); }
    } catch(error) {
      console.log("Error", error);
      setPodAmbassadorEmailError(EMAIL_VALIDATION_FAILED);
    } finally {
      setIsValidatingPodAmbassadorEmail(false);
    }
  }

  return {
    step1Watch,
    step1SetValue,
    register,
    userFields,
    addUser,
    handleSubmit,
    addUserHandler,
    removeUserHandler,
    errors,
    step2SetValue,
    architectureDiagramFile,
    onClickClearArchitectureFile,
    registerFunctions,
    step3Register,
    step3Watch,
    onSubmit,
    step,
    MAX_STEP,
    handleGoBack,
    setStep3Error,
    clearStep3Errors,
    usersList,
    updateUserList,
    step3SetValue,
    addUserLater,
    isStep3Submitted,
    isValidatingStep1,
    validatingFieldsStep1,
    isValidStep1,
    getValuesStep1,
    validatePodAbbreviation,
    isValidatingPod,
    podError,
    podAmbassadorEmailError,
    isValidatingPodAmbassadorEmail,
    validatePodAmbassadorEmail
  };
};

export const useOnboardingUserServices = ({ role, roles }: { role: string, roles: RoleItem[] }) => {
  const [services, setServices] = useState<ServicesEnum[]>([]);

  useEffect(() => {
    console.log({role, roles});

   if(!role) { setServices([]); }
    else {
      const sakeMakerAccessUserRole = ["Data Scientist", "Data Lead"];
      const dataBricksRoles = ["Data Engineer"];
      const roleIds = role.split(",").map(id => id.trim()).filter(id => id);
      // Try to find role names by sys_id first, if not found assume the input is already a role name
      const roleNames = roleIds.map(roleId => {
        const roleItem = roles.find((r) => r.sys_id === roleId);
        if (roleItem) {
          return roleItem.u_primary;
        }
        // If not found by sys_id, check if it's already a role name
        const roleByName = roles.find((r) => r.u_primary === roleId);
        return roleByName?.u_primary || roleId;
      }).filter(Boolean); 
      const serviceList = [];
      const hasSageMakerRole = roleNames.some(roleName => 
        sakeMakerAccessUserRole.includes(roleName as string)
      );
      const hasDataBricksRole = roleNames.some(roleName => 
        dataBricksRoles.includes(roleName as string)
      );
      if(hasSageMakerRole) { serviceList.push(ServicesEnum.SageMaker); }
      if(hasDataBricksRole) { serviceList.push(ServicesEnum.DataBricks); }
      setServices(serviceList);
    }
  }, [role, roles])

  return { services };
}
